import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Section, Eyebrow, H2, Lead, GlassCard, Reveal } from "../components/site/Primitives";
import StatusBadge from "../components/site/StatusBadge";
import { Heart, HelpingHand, Home as HomeIcon, AlertTriangle, ShieldCheck, BookOpen, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { log } from "../lib/logger";

const SERVICES = [
  "Transitional Housing", "Recovery Housing", "Affordable Housing", "Workforce Housing",
  "Behavioral Health Support", "Independent Living Support", "Case Management",
  "VA Benefits Navigation", "Job Placement", "Childcare On-Site", "Fitness & Wellness",
  "Counseling Rooms",
];

const ON_SITE = [
  { label: "Barbershop", icon: "✂︎" },
  { label: "Salon", icon: "💈" },
  { label: "Coffee Shop", icon: "☕" },
  { label: "Mini Grocery", icon: "🛒" },
  { label: "Vending", icon: "🥤" },
  { label: "Fitness Center", icon: "🏋︎" },
  { label: "Daycare", icon: "🧸" },
  { label: "Counseling", icon: "🤝" },
];

export default function VeteranHousing() {
  const [tab, setTab] = useState("apply");
  return (
    <div>
      <section className="relative min-h-[85vh] flex items-end pt-40 pb-24 grain overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1609220136736-443140cffec6?w=2200&q=80" alt="" className="w-full h-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/70 to-[#0A0A0A]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
        </div>
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-gold/10 rounded-full blur-[180px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-white/5 rounded-full blur-[160px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-gold mb-4">Flagship Pillar · 220 Inc.</div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold border border-gold/30 px-3 py-2 bg-black/40 mb-4">
            <ShieldCheck size={14} /> The Veteran Housing Initiative
          </div>
          <div className="mb-8"><StatusBadge kind="in-development" label="Planned Development · Phase 1–3" /></div>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95] max-w-5xl">
            A Promise to <span className="text-gold">Those Who Served</span>.
          </h1>
          <p className="mt-8 text-lg sm:text-2xl text-white/85 max-w-3xl leading-relaxed font-light">
            Dignified, service-rich housing for veterans — transitional, recovery, affordable, and
            workforce — designed to pair with behavioral health, childcare, fitness, and on-site essentials
            as the development comes online.
          </p>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 max-w-3xl">
            {[
              { k: "540+", l: "Veteran-Priority Units (Planned)" },
              { k: "12+", l: "Wraparound Services (Designed)" },
              { k: "24/7", l: "Case Management (Planned)" },
              { k: "100%", l: "Mission-Aligned" },
            ].map((s) => (
              <div key={s.l} className="bg-black/60 backdrop-blur-sm p-4 sm:p-5">
                <div className="font-display text-2xl sm:text-3xl text-gold">{s.k}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/55 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-sm text-white/55">
            VHI Contact · Travis R. McGruder · Founder &amp; CEO · <span className="text-white/80">Travis.McGruder@220inc.com</span>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <Section className="border-t border-white/5">
        <Eyebrow>The Initiative</Eyebrow>
        <H2 className="max-w-3xl">Four housing paths. One integrated community.</H2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: <HomeIcon />, title: "Transitional", body: "Short-term stabilization with on-site case management." },
            { icon: <Heart />, title: "Recovery", body: "Sober-living and clinical support in partnership with VA & behavioral health." },
            { icon: <HelpingHand />, title: "Affordable", body: "Workforce-affordable rents with veteran priority." },
            { icon: <BookOpen />, title: "Independent Living", body: "Long-term residences for stable, independent veteran tenants." },
          ].map((p) => (
            <GlassCard key={p.title} className="p-6">
              <div className="text-gold mb-4">{p.icon}</div>
              <div className="font-display text-xl mb-2">{p.title}</div>
              <p className="text-sm text-white/65 leading-relaxed">{p.body}</p>
            </GlassCard>
          ))}
        </div>
      </Section>

      {/* On-Site Amenities */}
      <Section className="border-t border-white/5">
        <Eyebrow>On-Site Community</Eyebrow>
        <H2 className="max-w-3xl">A neighborhood designed around residents.</H2>
        <Lead className="mt-4">
          Our community blueprint integrates dorm/barracks-style housing, apartment communities,
          and mixed-use developments — with services woven into daily life.
        </Lead>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
          {ON_SITE.map((a) => (
            <div key={a.label} className="border border-white/10 p-6 text-center hover:border-gold transition-colors">
              <div className="text-3xl mb-2 text-gold">{a.icon}</div>
              <div className="text-sm">{a.label}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Tabs: Apply / Donate / Volunteer / Emergency */}
      <Section className="border-t border-white/5">
        <div className="flex flex-wrap gap-2 border-b border-white/10 mb-10">
          {[
            { k: "apply", label: "Apply for Housing", icon: <HomeIcon size={14} /> },
            { k: "donate", label: "Donate", icon: <Heart size={14} /> },
            { k: "volunteer", label: "Volunteer", icon: <HelpingHand size={14} /> },
            { k: "emergency", label: "Emergency Request", icon: <AlertTriangle size={14} /> },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-5 py-3 text-sm inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                tab === t.k ? "border-gold text-gold" : "border-transparent text-white/60 hover:text-white"
              }`}
              data-testid={`vhi-tab-${t.k}`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === "apply" && <HousingApplyForm />}
        {tab === "donate" && <DonateForm />}
        {tab === "volunteer" && <VolunteerForm />}
        {tab === "emergency" && <EmergencyForm />}
      </Section>

      {/* Services */}
      <Section className="border-t border-white/5">
        <Eyebrow>Wraparound Services</Eyebrow>
        <H2 className="max-w-3xl">Housing is the foundation. Services make it permanent.</H2>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {SERVICES.map((s) => (
            <div key={s} className="bg-[#0c0c0e] p-5 text-sm text-white/85">{s}</div>
          ))}
        </div>
      </Section>

      {/* Partnerships */}
      <Section className="border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Reveal>
            <Eyebrow>Partnerships</Eyebrow>
            <H2>Built with the VA, nonprofits, and the community.</H2>
            <p className="mt-6 text-white/70 leading-relaxed">
              We co-design programs with VA medical centers, regional nonprofits, faith-based
              organizations, and city/county economic development teams. Public-private
              partnership is the operating model — not a marketing line.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <GlassCard className="p-8">
              <div className="font-display text-2xl mb-4">Partner Categories</div>
              <ul className="space-y-3 text-sm text-white/75">
                <li>· VA Medical Centers (housing referrals, behavioral health)</li>
                <li>· Veteran-serving nonprofits</li>
                <li>· Workforce boards & trade unions</li>
                <li>· County & municipal redevelopment agencies</li>
                <li>· Behavioral health providers</li>
                <li>· Faith-based community partners</li>
              </ul>
            </GlassCard>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}

function FormField({ label, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      <input className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold" {...props} />
    </label>
  );
}

function Select({ label, options, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      <select className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold" {...props}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function HousingApplyForm() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", veteran_status: "veteran", branch: "",
    discharge_status: "", housing_type: "transitional", household_size: 1, has_dependents: false,
    employment_status: "", monthly_income: "", desired_move_in: "", notes: "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, household_size: Number(f.household_size), monthly_income: f.monthly_income ? Number(f.monthly_income) : null };
      await api.post("/housing/apply", payload);
      toast.success("Application received. A counselor will reach out within 48 hours.");
      setF({ ...f, full_name: "", email: "", phone: "", notes: "" });
    } catch { toast.error("Could not submit. Please try again."); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="vhi-form-apply">
      <FormField label="Full Name *" required value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
      <FormField label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
      <FormField label="Phone *" required value={f.phone} onChange={(e) => set("phone", e.target.value)} />
      <Select label="Veteran Status" value={f.veteran_status} onChange={(e) => set("veteran_status", e.target.value)} options={[
        { v: "veteran", l: "Veteran" }, { v: "active_duty", l: "Active Duty" }, { v: "dependent", l: "Dependent" }, { v: "civilian", l: "Civilian" },
      ]} />
      <FormField label="Branch (if applicable)" value={f.branch} onChange={(e) => set("branch", e.target.value)} />
      <FormField label="Discharge Status (if applicable)" value={f.discharge_status} onChange={(e) => set("discharge_status", e.target.value)} />
      <Select label="Housing Type" value={f.housing_type} onChange={(e) => set("housing_type", e.target.value)} options={[
        { v: "transitional", l: "Transitional" }, { v: "recovery", l: "Recovery" }, { v: "affordable", l: "Affordable" }, { v: "workforce", l: "Workforce" },
      ]} />
      <FormField label="Household Size" type="number" min="1" value={f.household_size} onChange={(e) => set("household_size", e.target.value)} />
      <FormField label="Employment Status" value={f.employment_status} onChange={(e) => set("employment_status", e.target.value)} />
      <FormField label="Monthly Income (USD)" type="number" value={f.monthly_income} onChange={(e) => set("monthly_income", e.target.value)} />
      <FormField label="Desired Move-In Date" type="date" value={f.desired_move_in} onChange={(e) => set("desired_move_in", e.target.value)} />
      <label className="block md:col-span-2">
        <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Notes</span>
        <textarea rows={4} className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold"
          value={f.notes} onChange={(e) => set("notes", e.target.value)} />
      </label>
      <div className="md:col-span-2"><button className="btn-primary" type="submit" data-testid="vhi-apply-submit">Submit Application <ArrowRight size={14} /></button></div>
    </form>
  );
}

function DonateForm() {
  const [packages, setPackages] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [f, setF] = useState({
    pkg: "vh_100", custom: "", name: "", email: "",
    frequency: "one_time", designation: "veteran_housing", message: "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    api.get("/payments/packages").then((r) => {
      setPackages(r.data.donation_packages);
      setDesignations(r.data.designations);
    }).catch((e) => log.warn("payment packages fetch failed", e?.message));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      package_id: f.pkg === "custom" ? null : f.pkg,
      custom_amount: f.pkg === "custom" ? Number(f.custom) : null,
      donor_name: f.name,
      donor_email: f.email,
      frequency: f.frequency,
      designation: f.designation,
      message: f.message || null,
      origin_url: window.location.origin,
    };
    try {
      const { data } = await api.post("/payments/donations/checkout", payload);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="vhi-form-donate">
      <div className="md:col-span-2">
        <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-3">Select Amount</span>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {packages.map((p) => (
            <button type="button" key={p.id} onClick={() => set("pkg", p.id)}
              className={`p-3 text-sm border transition-colors ${
                f.pkg === p.id ? "border-gold text-gold bg-gold/5" : "border-white/10 text-white/70 hover:border-white/30"
              }`} data-testid={`donate-pkg-${p.id}`}>
              <div className="font-display text-lg">${p.amount}</div>
            </button>
          ))}
          <button type="button" onClick={() => set("pkg", "custom")}
            className={`p-3 text-sm border transition-colors ${
              f.pkg === "custom" ? "border-gold text-gold bg-gold/5" : "border-white/10 text-white/70 hover:border-white/30"
            }`} data-testid="donate-pkg-custom">
            <div className="font-display text-base">Custom</div>
          </button>
        </div>
        {f.pkg === "custom" && (
          <input type="number" min="1" placeholder="Enter amount (USD)" value={f.custom}
            onChange={(e) => set("custom", e.target.value)}
            className="mt-3 w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold"
            data-testid="donate-custom-amount" />
        )}
      </div>
      <FormField label="Name *" required value={f.name} onChange={(e) => set("name", e.target.value)} />
      <FormField label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
      <Select label="Frequency" value={f.frequency} onChange={(e) => set("frequency", e.target.value)} options={[
        { v: "one_time", l: "One-time" }, { v: "monthly", l: "Monthly" }, { v: "annual", l: "Annual" },
      ]} />
      <Select label="Designation" value={f.designation} onChange={(e) => set("designation", e.target.value)}
        options={designations.map((d) => ({ v: d.id, l: d.label }))} />
      <label className="block md:col-span-2">
        <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Message (optional)</span>
        <textarea rows={3} className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold"
          value={f.message} onChange={(e) => set("message", e.target.value)} />
      </label>
      <div className="md:col-span-2">
        <button className="btn-primary" type="submit" data-testid="vhi-donate-submit">
          Continue to Secure Checkout <Heart size={14} />
        </button>
        <div className="text-xs text-white/40 mt-3">Powered by Stripe · Test mode for V1. Receipts emailed automatically.</div>
      </div>
    </form>
  );
}

function VolunteerForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", availability: "evenings", skills: "", interests: "" });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/volunteers", {
        ...f,
        skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
        interests: f.interests.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Thank you for volunteering. We'll be in touch.");
      setF({ ...f, name: "", email: "", phone: "", skills: "", interests: "" });
    } catch { toast.error("Could not submit."); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="vhi-form-volunteer">
      <FormField label="Name *" required value={f.name} onChange={(e) => set("name", e.target.value)} />
      <FormField label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
      <FormField label="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
      <Select label="Availability" value={f.availability} onChange={(e) => set("availability", e.target.value)} options={[
        { v: "weekdays", l: "Weekdays" }, { v: "evenings", l: "Evenings" }, { v: "weekends", l: "Weekends" }, { v: "flexible", l: "Flexible" },
      ]} />
      <FormField label="Skills (comma-separated)" value={f.skills} onChange={(e) => set("skills", e.target.value)} />
      <FormField label="Areas of Interest (comma-separated)" value={f.interests} onChange={(e) => set("interests", e.target.value)} />
      <div className="md:col-span-2"><button className="btn-primary" type="submit" data-testid="vhi-volunteer-submit">Sign Up to Volunteer <HelpingHand size={14} /></button></div>
    </form>
  );
}

function EmergencyForm() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", veteran_status: "veteran", housing_type: "emergency",
    notes: "", emergency: true, household_size: 1,
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/housing/apply", f); toast.success("Emergency request received. Expect a call within hours."); }
    catch { toast.error("Could not submit. Please call our hotline."); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="vhi-form-emergency">
      <div className="md:col-span-2 border border-gold/30 bg-gold/5 p-4 text-sm text-white/80 flex items-start gap-3">
        <AlertTriangle className="text-gold flex-shrink-0 mt-0.5" size={18} />
        <span>If you are in immediate danger or experiencing a medical emergency, call 911. For the Veterans Crisis Line, dial 988 then press 1.</span>
      </div>
      <FormField label="Full Name *" required value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
      <FormField label="Phone *" required value={f.phone} onChange={(e) => set("phone", e.target.value)} />
      <FormField label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
      <Select label="Veteran Status" value={f.veteran_status} onChange={(e) => set("veteran_status", e.target.value)} options={[
        { v: "veteran", l: "Veteran" }, { v: "active_duty", l: "Active Duty" }, { v: "dependent", l: "Dependent" }, { v: "civilian", l: "Civilian" },
      ]} />
      <label className="block md:col-span-2">
        <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Briefly describe the situation</span>
        <textarea rows={4} required className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
      </label>
      <div className="md:col-span-2"><button className="btn-primary" type="submit" data-testid="vhi-emergency-submit">Send Urgent Request <AlertTriangle size={14} /></button></div>
    </form>
  );
}
